import React, { useState, useEffect } from 'react';
import { 
  TextField, Button, MenuItem, Typography, Box, Alert, Container, CircularProgress, 
  Grid, Card, CardContent, CardHeader, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon, Cancel as CancelIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import API from '../../../services/api';

import dayjs from 'dayjs';

// Função principal de cálculo de prazo conforme código de despacho
function calcularPrazoAgenda(dataBase, prazoDespacho) {
  if (!prazoDespacho) {
    return { dataLimite: '', descricao: 'Código de despacho não encontrado.' };
  }
  
  let dataLimite = dayjs(dataBase);
  
  if (prazoDespacho.dias) {
    dataLimite = dataLimite.add(prazoDespacho.dias, 'day');
  } else if (prazoDespacho.anos) {
    dataLimite = dataLimite.add(prazoDespacho.anos, 'year');
  }
  
  return {
    dataLimite: dataLimite.format('DD-MM-YYYY'),
    descricao: prazoDespacho.descricao,
    tipo: prazoDespacho.tipo,
    prazoDias: prazoDespacho.dias || '',
    prazoAnos: prazoDespacho.anos || '',
    prazoExtraordinario: prazoDespacho.prazo_extraordinario || '',
  };
}

const CalculadoraPrazo = () => {
  const [data, setData] = useState(dayjs().format('YYYY-MM-DD'));
  const [prazoDespachoId, setPrazoDespachoId] = useState('');
  const [prazosDespachos, setPrazosDespachos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [showResult, setShowResult] = useState(false);
  
  // Estados para gerenciamento de códigos
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit'
  const [selectedCodigo, setSelectedCodigo] = useState(null);
  const [formData, setFormData] = useState({
    codigo: '',
    descricao: '',
    dias: '',
    anos: '',
    tipo: 'ORDINARIO',
    prazo_extraordinario: ''
  });

  // Buscar prazos de despachos da API
  useEffect(() => {
    fetchPrazosDespachos();
  }, []);

  const handleCalcular = (e) => {
    e.preventDefault();
    if (!data || !prazoDespachoId) return;
    
    const prazoSelecionado = prazosDespachos.find(p => p.id.toString() === prazoDespachoId);
    const res = calcularPrazoAgenda(data, prazoSelecionado);
    setResultado(res);
    setShowResult(true);
  };

  // Funções para gerenciamento de códigos
  const openCreateDialog = () => {
    setDialogMode('create');
    setFormData({
      codigo: '',
      descricao: '',
      dias: '',
      anos: '',
      tipo: 'ORDINARIO',
      prazo_extraordinario: '',
      ativo: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (codigo) => {
    setDialogMode('edit');
    setSelectedCodigo(codigo);
    setFormData({
      codigo: codigo.codigo,
      descricao: codigo.descricao,
      dias: codigo.dias || '',
      anos: codigo.anos || '',
      tipo: codigo.tipo || 'ORDINARIO',
      prazo_extraordinario: codigo.prazoExtraordinario || '',
      ativo: codigo.ativo !== undefined ? codigo.ativo : true
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCodigo(null);
    setFormData({
      codigo: '',
      descricao: '',
      dias: '',
      anos: '',
      tipo: 'ORDINARIO',
      prazo_extraordinario: '',
      ativo: true
    });
  };

  const handleFormSubmit = async () => {
    try {
      setLoading(true);
      
      if (dialogMode === 'create') {
        const response = await API.post('/agenda/prazos-despachos', {
          codigo: formData.codigo,
          descricao: formData.descricao,
          dias: formData.dias ? parseInt(formData.dias) : null,
          anos: formData.anos ? parseInt(formData.anos) : null,
          tipo: formData.tipo,
          prazo_extraordinario: formData.prazo_extraordinario ? parseInt(formData.prazo_extraordinario) : null,
          ativo: formData.ativo !== undefined ? formData.ativo : true
        });

      } else if (dialogMode === 'edit') {
        const response = await API.patch(`/agenda/prazos-despachos/${selectedCodigo.codigo}`, {
          descricao: formData.descricao,
          dias: formData.dias ? parseInt(formData.dias) : null,
          anos: formData.anos ? parseInt(formData.anos) : null,
          tipo: formData.tipo,
          prazo_extraordinario: formData.prazo_extraordinario ? parseInt(formData.prazo_extraordinario) : null,
          ativo: formData.ativo !== undefined ? formData.ativo : true
        });

      }
      
      // Fechar o dialog primeiro
      handleCloseDialog();
      
      // Aguardar um pouco para garantir que a operação foi processada no backend
      setTimeout(async () => {
        // Recarregar dados após a operação
        await fetchPrazosDespachos();
      }, 500);
      
    } catch (err) {

      setError(err.response?.data?.message || 'Erro ao salvar código de despacho');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (prazo) => {
    // Use window.confirm em vez de confirm para evitar erro do ESLint
    if (!window.confirm(`Tem certeza que deseja deletar o código ${prazo.codigo}?`)) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await API.delete(`/agenda/prazos-despachos/${prazo.codigo}`);

      // Aguardar um pouco para garantir que a operação foi processada no backend
      setTimeout(async () => {
        // Recarregar dados após a exclusão
        await fetchPrazosDespachos();
      }, 500);
      
    } catch (err) {

      setError(err.response?.data?.message || 'Erro ao excluir código de despacho');
    } finally {
      setLoading(false);
    }
  };

  // Extrair a função fetchPrazosDespachos para reutilização
  const fetchPrazosDespachos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Buscar todos os códigos ativos para usar na calculadora
      const response = await API.get('/agenda/prazos-despachos', {
        params: {
          ativo: 'true', // Buscar apenas códigos ativos
          limit: 1000, // Aumentar o limite para buscar mais códigos
          page: 1,
          orderBy: 'codigo',
          orderDirection: 'asc'
        }
      });

      if (response.data && response.data.items) {
        setPrazosDespachos(response.data.items);

      } else if (response.data && Array.isArray(response.data)) {
        // Caso a API retorne um array direto
        setPrazosDespachos(response.data);
      } else {
        setPrazosDespachos([]);

      }
    } catch (err) {

      setError('Erro ao carregar os códigos de despacho. Verifique sua conexão.');
      setPrazosDespachos([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ background: '#f5f5f5', minHeight: '100vh', py: 0, px: 0 }}>
      <Container maxWidth={false} disableGutters sx={{ height: '100%', minHeight: '100vh', px: { xs: 1, md: 6 }, py: { xs: 2, md: 6 }, overflowX: 'auto' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 4, textAlign: 'left' }}>
          Calculadora de Prazo por Código de Despacho
        </Typography>
        
        {/* Mostrar loading ou erro */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Carregando códigos de despacho...</Typography>
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'flex-start' }}>
          <Grid container spacing={4}>
            {/* Calculadora - Lado esquerdo */}
            <Grid item xs={12} md={8}>
              <Box sx={{ width: '100%', background: '#fff', borderRadius: 3, boxShadow: 2, p: { xs: 2, md: 5 }, minWidth: 0, overflowX: 'auto' }}>
                <form onSubmit={handleCalcular}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 2 }}>
                    <TextField
                      label="Data base"
                      type="date"
                      value={data}
                      onChange={e => setData(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      required
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Código de Despacho"
                      select
                      value={prazoDespachoId}
                      onChange={e => setPrazoDespachoId(e.target.value)}
                      required
                      disabled={loading || prazosDespachos.length === 0}
                      sx={{ flex: 1 }}
                      placeholder="Selecione o código"
                    >
                      <MenuItem value="" disabled>
                        {loading ? 'Carregando...' : 'Selecione...'}
                      </MenuItem>
                      {prazosDespachos.map((prazo) => (
                        <MenuItem key={prazo.id} value={prazo.id.toString()}>
                          {prazo.codigo} - {prazo.descricao}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button 
                      variant="contained" 
                      type="submit" 
                      disabled={loading || !data || !prazoDespachoId}
                      sx={{ minWidth: 150, height: 56 }}
                    >
                      Calcular
                    </Button>
                  </Box>
                  {showResult && resultado && (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        label="Data Limite"
                        value={resultado.dataLimite}
                        InputProps={{ readOnly: true }}
                        sx={{ background: '#ededed', width: '100%', mb: 1 }}
                      />
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <b>Descrição:</b> {resultado.descricao || '-'}<br/>
                        <b>Tipo:</b> {resultado.tipo || '-'}<br/>
                        {resultado.prazoDias && (<span><b>Dias:</b> {resultado.prazoDias}<br/></span>)}
                        {resultado.prazoAnos && (<span><b>Anos:</b> {resultado.prazoAnos}<br/></span>)}
                        {resultado.prazoExtraordinario && (<span><b>Prazo Extraordinário:</b> {resultado.prazoExtraordinario} dias<br/></span>)}
                      </Typography>
                    </Box>
                  )}
                </form>
                <Box mt={3}>
                  <Alert severity="error" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Cuidado com o prazo!
                  </Alert>
                  <Typography variant="body2" color="textSecondary">
                    O cálculo é feito conforme as regras do sistema de agenda utilizando dados atualizados do banco de dados.<br />
                    Os prazos são calculados em dias corridos. Para demandas no INPI, caso o prazo final seja em final de semana ou feriado, ficará prorrogado automaticamente para o primeiro dia útil seguinte.<br />
                    Os códigos são carregados dinamicamente do sistema e podem ser gerenciados pelos administradores.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Gerenciamento de Códigos - Lado direito */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: 'fit-content' }}>
                <CardHeader
                  title="Gerenciar Códigos"
                  action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={fetchPrazosDespachos}
                        disabled={loading}
                        title="Atualizar lista"
                      >
                        <RefreshIcon />
                      </IconButton>
                      <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        size="small"
                        onClick={openCreateDialog}
                        disabled={loading}
                      >
                        Novo Código
                      </Button>
                    </Box>
                  }
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Total: {prazosDespachos.length} códigos
                  </Typography>
                  
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                      {prazosDespachos.map((prazo) => (
                        <Card key={prazo.id} variant="outlined" sx={{ mb: 1, p: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, pr: 1 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {prazo.codigo}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {prazo.descricao}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip label={prazo.tipo} size="small" variant="outlined" />
                                {prazo.dias && <Chip label={`${prazo.dias}d`} size="small" />}
                                {prazo.anos && <Chip label={`${prazo.anos}a`} size="small" />}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <IconButton size="small" onClick={() => openEditDialog(prazo)} disabled={loading}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDelete(prazo)} disabled={loading}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Dialog para Criar/Editar Código */}
          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
            <DialogTitle>
              {selectedCodigo ? 'Editar Código' : 'Criar Novo Código'}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Código"
                      value={formData.codigo}
                      onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Tipo"
                      select
                      value={formData.tipo}
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                      fullWidth
                      required
                    >
                      <MenuItem value="ORDINARIO">Ordinário</MenuItem>
                      <MenuItem value="EXTRAORDINARIO">Extraordinário</MenuItem>
                      <MenuItem value="INFORMATIVO">Informativo</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Descrição"
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Dias"
                      type="number"
                      value={formData.dias}
                      onChange={(e) => setFormData({ ...formData, dias: e.target.value })}
                      fullWidth
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Meses"
                      type="number"
                      value={formData.meses}
                      onChange={(e) => setFormData({ ...formData, meses: e.target.value })}
                      fullWidth
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Anos"
                      type="number"
                      value={formData.anos}
                      onChange={(e) => setFormData({ ...formData, anos: e.target.value })}
                      fullWidth
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Prazo Extraordinário (dias)"
                      type="number"
                      value={formData.prazo_extraordinario}
                      onChange={(e) => setFormData({ ...formData, prazo_extraordinario: e.target.value })}
                      fullWidth
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <TextField
                        label="Ativo"
                        select
                        value={formData.ativo}
                        onChange={(e) => setFormData({ ...formData, ativo: e.target.value === 'true' })}
                        size="small"
                        sx={{ minWidth: 100 }}
                      >
                        <MenuItem value="true">Sim</MenuItem>
                        <MenuItem value="false">Não</MenuItem>
                      </TextField>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="inherit">
                Cancelar
              </Button>
              <Button 
                onClick={handleFormSubmit} 
                variant="contained" 
                disabled={loading || !formData.codigo || !formData.descricao || !formData.tipo}
              >
                {selectedCodigo ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Box>
  );
};

export default CalculadoraPrazo;
